import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  FileText,
  Shield,
  Zap,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import * as _ from "lodash";
import { emailPolicy } from "../../utils/auth";
import loginRequestLogsService, {
  LoginRequestData,
  SocialLoginProvider,
} from "../../services/login-request-logs.service";

interface LoginFormInputs {
  email: string;
  password: string;
}

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [loginData, setLoginData] = useState<LoginRequestData | null>(null);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  }: any = useForm();

  useEffect(() => {
    fetchLoginOptions();

    const params = new URLSearchParams(window.location.search);
    const errorMsg = params.get("error");

    if (errorMsg) {
      const decodedError = decodeURIComponent(errorMsg);

      const hasShown = sessionStorage.getItem("loginErrorShown");
      if (!hasShown) {
        toast.error(decodedError);
        sessionStorage.setItem("loginErrorShown", "true");
        setTimeout(() => {
          sessionStorage.removeItem("loginErrorShown");
        }, 2000);
      }
      const newUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }

    return () => {
      sessionStorage.removeItem("loginErrorShown");
    };
  }, []);

  const fetchLoginOptions = async () => {
    try {
      setIsLoadingOptions(true);
      const response = await loginRequestLogsService.createLoginRequestLog();
      setLoginData(response.data);
    } catch (error) {
      console.error("Error fetching login options:", error);
      toast.error("Failed to load login options");
      // Set default to show classical login if API fails
      setLoginData({
        _id: "",
        createdAt: "",
        updatedAt: "",
        classical_provider: true,
        social_provider: [],
      });
    } finally {
      setIsLoadingOptions(false);
    }
  };

  const onSubmit: SubmitHandler<LoginFormInputs> = async (data) => {
    console.log(data);

    setLoading(true);
    const form = document.createElement("form");
    form.action = `${import.meta.env.VITE_DMS_API}/dms-v2-srv/user-srv/login`;
    form.method = "POST";

    const usernameInput = document.createElement("input");
    usernameInput.type = "hidden";
    usernameInput.name = "email";
    usernameInput.value = _.escape(data.email);
    form.appendChild(usernameInput);

    const passwordInput = document.createElement("input");
    passwordInput.type = "hidden";
    passwordInput.name = "password";
    passwordInput.value = data.password;
    form.appendChild(passwordInput);

    form.onload = async () => {
      try {
        if (form.status === 200) {
        } else {
          console.error("Login failed. Status:", form.status);
        }
      } catch (error) {
        console.log(error, "error");

        console.error("Error during login:", error);
      } finally {
        setLoading(false);
        form.remove();
      }
    };
    document.body.appendChild(form);
    form.submit();
  };

  const handleSocialLogin = (provider: SocialLoginProvider) => {
    // Redirect to the social provider's redirect_url in the same tab
    window.location.href = provider.redirect_url;
  };

  const handleSkillmineAuth = () => {
    return console.log("Auth redirect URI");
    // Dynamically construct the Auth0 URL using current window location
    const currentOrigin = window.location.origin;
    const redirectUri = `${currentOrigin}/auth/callback`;

    const authParams = new URLSearchParams({
      client_id: "b629a295-d744-494d-88a5-dd9ccec9cfda",
      response_type: "token",
      scope: "openid profile user_info_all",
      redirect_uri: redirectUri,
      groups_info: "0",
      response_mode: "query",
    });

    const authUrl = `https://smtc-api.auth-zero.com/authz-srv/authz?${authParams.toString()}`;

    // Log for debugging
    console.log("Auth redirect URI:", redirectUri);

    // Redirect to Skillmine Auth0
    window.location.href = authUrl;
  };

  const features = [
    {
      icon: FileText,
      title: "DocIQ",
      description: "Organize and process documents with AI-powered automation",
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "Bank-grade security with role-based access control",
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Process thousands of documents in minutes, not hours",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-800 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: "60px 60px",
        }}
      ></div>

      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 lg:gap-12 relative z-10">
        {/* Left Side - Features */}
        <>
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="hidden lg:flex flex-col justify-center space-y-8"
          >
            <div className="text-white">
              <h1 className="text-4xl xl:text-4xl font-bold mb-4 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                DocIQ
              </h1>
              <p className="text-xl text-blue-100 mb-12">
                Transform your document workflow with AI-powered automation and
                intelligent processing.
              </p>
            </div>

            <div className="space-y-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.6 }}
                  className="flex items-start space-x-4 p-4 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20"
                >
                  <div className="p-2 rounded-lg bg-blue-500/20">
                    <feature.icon className="h-6 w-6 text-blue-300" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-blue-100 text-sm">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex items-center justify-center"
          >
            <div className="w-full max-w-md">
              <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-white/20">
                {/* Logo and Title */}
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Welcome Back
                  </h2>
                  <p className="text-gray-600">Sign in to your DocIQ account</p>
                </div>

                {/* Loading State */}
                {isLoadingOptions ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
                    <p className="mt-4 text-gray-600">
                      Loading login options...
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Classical Login Form */}
                    {loginData?.classical_provider && (
                      <form
                        onSubmit={handleSubmit(onSubmit)}
                        className="space-y-6"
                      >
                        {/* Email Field */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Mail className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              id="email"
                              type="email"
                              placeholder="Enter your email"
                              autoComplete="email"
                              {...register("email", {
                                required: "Email is required",
                                validate: (value: any) => {
                                  const validationResult = emailPolicy(value);
                                  return (
                                    validationResult === true ||
                                    validationResult
                                  );
                                },
                              })}
                              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                            />
                          </div>
                          {errors.email && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.email.message}
                            </p>
                          )}
                        </div>

                        {/* Password Field */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Password
                          </label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <Lock className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                              id="password"
                              type={showPassword ? "text" : "password"}
                              placeholder="Enter your password"
                              autoComplete="current-password"
                              {...register("password", {
                                required: "Password is required",
                              })}
                              className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            >
                              {showPassword ? (
                                <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                              ) : (
                                <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                              )}
                            </button>
                          </div>
                          {errors.password && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.password.message}
                            </p>
                          )}
                        </div>

                        {/* Remember Me and Forgot Password */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              {...register("rememberMe")}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label className="ml-2 block text-sm text-gray-700">
                              Remember me
                            </label>
                          </div>
                          <button
                            type="button"
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            onClick={() => navigate("/forgot-password")}
                          >
                            Forgot password?
                          </button>
                        </div>

                        {/* Submit Button */}
                        <button
                          type="submit"
                          disabled={loading}
                          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200 transform hover:scale-[1.02]"
                        >
                          {loading && (
                            <div className="inline-block h-4 w-4 me-2 mt-1 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
                          )}
                          LOGIN
                        </button>
                      </form>
                    )}

                    {/* Social Login Section */}
                    {loginData?.social_provider &&
                      loginData.social_provider.length > 0 && (
                        <div className="space-y-4">
                          {/* Divider if both classical and social are available */}
                          {loginData.classical_provider && (
                            <div className="relative my-6">
                              <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300"></div>
                              </div>
                              <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500">
                                  OR
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Dynamic Social Login Buttons */}
                          <div className="space-y-3">
                            {loginData.social_provider
                              .filter((provider) => provider.is_active)
                              .map((provider) => (
                                <button
                                  key={provider.provider_name}
                                  type="button"
                                  onClick={() => handleSocialLogin(provider)}
                                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition duration-200 transform hover:scale-[1.02] flex items-center justify-center space-x-3"
                                >
                                  <div className="flex-shrink-0">
                                    {provider.logo_url ? (
                                      <img
                                        src={provider.logo_url}
                                        alt={provider.provider_name}
                                        className="w-5 h-5 rounded object-cover"
                                        onError={(e) => {
                                          e.currentTarget.style.display =
                                            "none";
                                          e.currentTarget.nextElementSibling?.classList.remove(
                                            "hidden",
                                          );
                                        }}
                                      />
                                    ) : null}
                                    <Shield
                                      className={`w-5 h-5 ${
                                        provider.logo_url ? "hidden" : ""
                                      }`}
                                    />
                                  </div>
                                  <span>
                                    Continue with{" "}
                                    {provider.provider_name.replace(/_/g, " ")}
                                  </span>
                                  <ExternalLink className="w-4 h-4" />
                                </button>
                              ))}
                          </div>
                        </div>
                      )}

                    {/* Fallback Skillmine Auth if no dynamic providers */}
                    {(!loginData?.social_provider ||
                      loginData.social_provider.length === 0) && (
                      <>
                        <div className="relative my-6">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300"></div>
                          </div>
                          <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-white text-gray-500">
                              OR
                            </span>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={handleSkillmineAuth}
                          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition duration-200 transform hover:scale-[1.02] flex items-center justify-center space-x-2"
                        >
                          <Shield className="w-5 h-5" />
                          <span>Login with Skillmine auth</span>
                        </button>
                      </>
                    )}
                  </>
                )}

                {/* Additional Links */}
                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    Don't have an account?{" "}
                    <button
                      onClick={() => navigate("/register")}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Sign up
                    </button>
                  </p>
                </div>
              </div>

              {/* Mobile Features */}
              <div className="lg:hidden mt-8 space-y-4">
                {features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + index * 0.1, duration: 0.6 }}
                    className="flex items-center space-x-3 text-white"
                  >
                    <feature.icon className="h-5 w-5 text-blue-300" />
                    <span className="text-sm">{feature.title}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </>
      </div>
    </div>
  );
};

export default Login;
